import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AlertTriangle, ChevronDown, Lock, Sparkles, Save, Plus, Loader2, CheckCircle2 } from 'lucide-react';
import toast from 'react-hot-toast';
import axiosInstance from '../../api/axios';
import { resolve403ErrorMessage } from '../../request/getApiErrorMessage.ts';
import { saveTestPlan, testStepsToPlanPayload } from '../../api/controlTestPlan';
import {
  generateTestStepsStream,
  buildGenerateTestStepsRequest,
} from '../../features/controls/generateTestStepsStream.ts';
import { mergeGeneratedPopulationRequirement } from '../../features/controls/populationRequirement.ts';
import OverviewReadOnly from './OverviewReadOnly';
import PopulationRequirementPanel from './PopulationRequirementPanel';
import TestAttributesGenerator from './TestAttributesGenerator';
import AttributeFieldMapping from './AttributeFieldMapping';

/* ---- step preparation (one test step = one attribute row) ---- */
function descToLines(text) {
  return String(text || '')
    .split(/\n|(?<=[.;])\s+/)
    .map(s => s.trim())
    .filter(Boolean);
}
function prepareSteps(steps) {
  return (steps || []).map((s, i) => {
    const summary = s.summary || s.description || s._text || '';
    const keyPoints = Array.isArray(s.key_points) && s.key_points.length > 0
      ? s.key_points : descToLines(s.description || s.summary);
    return { ...s, _text: summary, summary, key_points: keyPoints, step_number: i + 1 };
  });
}

// No opinionated defaults. The Attribute Testing Table starts blank so the
// auditor uses "Suggest Fields" (AI, control-specific) or "Add Field" to define
// exactly which fields are validated from evidence. Avoids wrong/irrelevant
// defaults being saved against a control.
const DEFAULT_FIELDS = [];

function Section({ title, icon, open, onToggle, children, locked, lockMessage, actions }) {
  return (
    <div className={`bg-white rounded-xl shadow-sm border overflow-hidden ${locked ? 'border-gray-100' : 'border-gray-200'}`}>
      <div className={`w-full flex items-center justify-between px-6 py-3 ${locked ? 'bg-gray-400' : 'bg-blue-600'}`}>
        <button
          type="button"
          onClick={locked ? undefined : onToggle}
          disabled={locked}
          className="flex items-center gap-2 flex-1 text-left disabled:cursor-not-allowed"
        >
          {locked ? <Lock className="h-4 w-4 text-white" /> : icon}
          <span className="text-sm font-semibold text-white uppercase tracking-wide">{title}</span>
          {locked && lockMessage && (
            <span className="ml-2 text-xs text-white/80 font-normal normal-case">— {lockMessage}</span>
          )}
          {!locked && (
            <ChevronDown className={`h-4 w-4 text-blue-200 ml-1 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
          )}
        </button>
        {!locked && actions && <div className="flex items-center gap-2 ml-4">{actions}</div>}
      </div>
      {open && !locked && <div>{children}</div>}
    </div>
  );
}

/**
 * TestDesignTab — "Test Plan" tab on the control detail page.
 * Owns the generation + field-table state so action buttons can live on the
 * section headers (per design).
 */
export default function TestDesignTab({
  control,
  controlUuid,
  testSteps,
  onStepChange,
  onReplaceSteps,
  handleSaveSteps,
  stepsModified,
  stepsSaving,
  linkedRisks,
  linkedRisksLoading,
  onControlUpdated,
}) {
  const [open, setOpen] = useState({ overview: false, attributes: false, table: false });
  const toggle = (k) => setOpen(p => ({ ...p, [k]: !p[k] }));

  /* ---------- Test Attributes generation state ---------- */
  const [genPhase, setGenPhase] = useState(
    (testSteps || []).length > 0 ? 'generated' : 'idle'
  );
  const [genProgress, setGenProgress] = useState([]);
  const [genError, setGenError] = useState('');

  useEffect(() => {
    if (genPhase !== 'generating') {
      setGenPhase((testSteps || []).length > 0 ? 'generated' : 'idle');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testSteps]);

  const runGenerate = async () => {
    if ((testSteps || []).length > 0 || fields.filter(f => !f.ai_suggested).length > 0) {
      const ok = window.confirm(
        'Re-generating will replace all current attributes and steps.\n\n' +
        'Any custom fields you have saved will remain, but their attribute assignments may need to be updated.\n\n' +
        'Continue?'
      );
      if (!ok) return;
    }
    setGenError('');
    setGenPhase('generating');
    setGenProgress(['Connecting to AI…']);
    const push = (msg) => setGenProgress(prev => (prev[prev.length - 1] === msg ? prev : [...prev, msg]));
    try {
      const result = await generateTestStepsStream(
        buildGenerateTestStepsRequest(control || {}),
        { onStatus: (p) => { if (p.message) push(p.message); } },
      );
      onReplaceSteps?.(prepareSteps(result.test_steps || []));
      // Keep the AI's "does this control need a population?" decision. An earlier
      // auditor override is preserved (merge returns null in that case).
      const popAttrs = mergeGeneratedPopulationRequirement(control?.sampling_attributes, result);
      if (popAttrs && controlUuid) {
        try {
          await axiosInstance.put(`/api/sox-controls/${controlUuid}`, { sampling_attributes: popAttrs });
          onControlUpdated?.();
        } catch (popErr) {
          console.warn('Saving population suggestion failed:', popErr.message);
        }
      }
      push('Done!');
      setGenPhase('generated');
    } catch (err) {
      setGenError(resolve403ErrorMessage(err, err.message || 'Generation failed'));
      setGenPhase((testSteps || []).length > 0 ? 'generated' : 'idle');
    }
  };

  const attrCount = (testSteps || []).length;

  /* ---------- Attribute Testing Table (fields) state ---------- */
  const [fields, setFields] = useState(() => control?.attribute_table_config?.fields || DEFAULT_FIELDS);
  const [fieldsDirty, setFieldsDirty] = useState(false);
  const [fieldsSaving, setFieldsSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestError, setSuggestError] = useState('');
  const fieldsRef = useRef(fields);
  const saveTimerRef = useRef(null);
  const skipAutoSaveRef = useRef(false);

  fieldsRef.current = fields;

  useEffect(() => {
    if (skipAutoSaveRef.current) {
      skipAutoSaveRef.current = false;
      return;
    }
    const saved = control?.attribute_table_config?.fields;
    if (Array.isArray(saved)) setFields(saved);
  }, [control?.attribute_table_config]);

  // Persist a specific field set (used by Suggest Fields, auto-save, and manual Save).
  const persistFields = useCallback(async (nextFields) => {
    setFieldsSaving(true);
    try {
      await axiosInstance.put(`/api/sox-controls/${controlUuid}`, {
        attribute_table_config: {
          ...(control?.attribute_table_config || {}),
          fields: nextFields,
        },
      });
      // Sync fields to the test plan table ONLY when testSteps is non-empty.
      // Guard is required: testStepsToPlanPayload([], fields) sends attributes:[] which
      // would supersede the active plan version and wipe all A/B/C/D test attributes.
      // Fields are still saved to attribute_table_config above (different PUT path) and
      // will be picked up by the backend lazy migration when a test is run.
      if (controlUuid && (testSteps || []).length > 0) {
        try {
          await saveTestPlan(controlUuid, testStepsToPlanPayload(testSteps, nextFields));
        } catch (syncErr) {
          // Any failure (not only 403) must be visible: a rejected plan save leaves the previous plan in
          // place, and runs keep using it without any sign of what went wrong.
          toast.error(
            'Failed to save test plan: ' +
              resolve403ErrorMessage(syncErr, syncErr.response?.data?.error || syncErr.message),
            { id: 'test-plan-save-error' },
          );
        }
      }
      setFieldsDirty(false);
      skipAutoSaveRef.current = true;
      onControlUpdated?.();
      return true;
    } catch (err) {
      toast.error(
        'Failed to save: ' + resolve403ErrorMessage(err, err.response?.data?.error || err.message),
        { id: 'test-plan-save-error' },
      );
      return false;
    } finally { setFieldsSaving(false); }
  }, [controlUuid, control?.attribute_table_config, onControlUpdated, testSteps]);

  const scheduleFieldSave = useCallback((nextFields) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      void persistFields(nextFields);
    }, 600);
  }, [persistFields]);

  useEffect(() => () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
  }, []);

  const updateFields = (updater) => {
    setFields(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      setFieldsDirty(true);
      scheduleFieldSave(next);
      return next;
    });
  };

  const suggestFields = async () => {
    setSuggesting(true);
    setSuggestError('');
    try {
      // One attribute per step (matches how TestDesignTab counts attrCount)
      const attributes = (testSteps || []).map((step, i) => ({
        ref: step.ref || String.fromCharCode(65 + i),
        label: step.summary || step.description || step._text || `Attribute ${String.fromCharCode(65 + i)}`,
        description: step.description || step.summary || '',
      }));
      const resp = await axiosInstance.post('/api/test-runs/suggest-fields', {
        control: control || {},
        attributes,
      });
      // Build ref ("A","B"…) → step id map so AI-returned refs map to real IDs.
      const refToId = {};
      (testSteps || []).forEach((s, i) => {
        const ref = s.ref || String.fromCharCode(65 + i);
        refToId[ref] = s.id || String(i);
      });
      const suggested = (resp.data?.fields || []).map(f => {
        const mappedIds = (f.attribute_ids || []).map(r => refToId[r] || r).filter(Boolean);
        return {
          id: f.id || (f.label || '').toLowerCase().replace(/\s+/g, '_'),
          label: f.label || '',
          description: f.description || '',
          // AI returns refs like ["A","B"]; checkboxes need the step's actual id.
          // Empty array means applies to all — keep as-is.
          attribute_ids: mappedIds,
          pass_condition: f.pass_condition || '',
          ai_suggested: true,
        };
      });
      if (suggested.length > 0) {
        skipAutoSaveRef.current = true;
        setFields(suggested);
        setFieldsDirty(false);
        await persistFields(suggested);
      }
    } catch (err) {
      // The axios interceptor already shows a toast for errors with a `detail`
      // field (Anthropic / provider errors).  Show an inline message here as a
      // fallback so the user always sees feedback, but avoid a native alert().
      const msg = resolve403ErrorMessage(err, err.response?.data?.detail || err.response?.data?.error || err.message || 'Field suggestion failed.');
      setSuggestError(msg);
    } finally { setSuggesting(false); }
  };
  const addField      = () => updateFields(p => [...p, { id: 'field_' + Date.now(), label: '', description: '', attribute_ids: [], pass_condition: '', ai_suggested: false }]);
  const renameField   = (id, label) => updateFields(p => p.map(f => f.id === id ? { ...f, label, ai_suggested: false } : f));
  const describeField = (id, description) => updateFields(p => p.map(f => f.id === id ? { ...f, description, ai_suggested: false } : f));
  const deleteField   = (id) => updateFields(p => p.filter(f => f.id !== id));
  const scopeField    = (id, attrId, checked) => updateFields(p => p.map(f => {
    if (f.id !== id) return f;
    const ids = f.attribute_ids || [];
    return { ...f, attribute_ids: checked ? [...ids.filter(a => a !== attrId), attrId] : ids.filter(a => a !== attrId), ai_suggested: false };
  }));
  const setPassCondition = (id, value) => updateFields(p => p.map(f => f.id === id ? { ...f, pass_condition: value, ai_suggested: false } : f));
  const saveFields = () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    return persistFields(fieldsRef.current);
  };

  // Attribute Testing Table unlocks once attributes exist and are saved.
  const hasAttributes = (testSteps || []).length > 0 && !stepsModified;

  const handleSaveAttributes = async () => {
    await handleSaveSteps();
    if (controlUuid) {
      try {
        await saveTestPlan(controlUuid, testStepsToPlanPayload(testSteps, fieldsRef.current));
      } catch (err) {
        toast.error(
          'Failed to save test plan: ' +
            resolve403ErrorMessage(err, err.response?.data?.error || err.message),
          { id: 'test-plan-save-error' },
        );
      }
    }
    setOpen(p => ({ ...p, table: true }));
    onControlUpdated?.();
  };

  const iconFor = (path) => (
    <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d={path} />
    </svg>
  );

  const btnWhite = 'px-3 py-1 text-xs bg-white text-blue-600 font-medium rounded-md hover:bg-blue-50 disabled:opacity-50 inline-flex items-center gap-1';
  const btnGhost = 'px-3 py-1 text-xs text-white border border-white/40 rounded-md hover:bg-blue-700 inline-flex items-center gap-1';

  return (
    <div className="space-y-4">

      {/* 1. Control Overview (read-only) */}
      <Section
        title="Control Overview"
        open={open.overview}
        onToggle={() => toggle('overview')}
        icon={iconFor('M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z')}
      >
        <OverviewReadOnly control={control} linkedRisks={linkedRisks} linkedRisksLoading={linkedRisksLoading} />
      </Section>

      {/* Population requirement — AI-suggested, auditor-overridable */}
      <PopulationRequirementPanel
        control={control}
        controlUuid={controlUuid}
        onControlUpdated={onControlUpdated}
      />

      {/* 2. Test Attributes & Procedures — actions on the header */}
      <Section
        title="Test Attributes & Procedures"
        open={open.attributes}
        onToggle={() => toggle('attributes')}
        icon={iconFor('M4 6h16M4 10h16M4 14h10')}
        actions={genPhase === 'generated' ? (
          <>
            <span className="text-xs text-white/80 font-normal normal-case">
              {attrCount} attribute{attrCount !== 1 ? 's' : ''}
            </span>
            <button type="button" onClick={runGenerate} className={btnGhost}>
              <Sparkles className="h-3 w-3" /> Regenerate
            </button>
            {stepsModified ? (
              <button type="button" onClick={handleSaveAttributes} disabled={stepsSaving} className={btnWhite}>
                <Save className="h-3 w-3" /> {stepsSaving ? 'Saving…' : 'Accept & Save'}
              </button>
            ) : attrCount > 0 ? (
              <span className="px-2 py-1 text-xs text-white/90 inline-flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" /> Saved
              </span>
            ) : null}
          </>
        ) : null}
      >
        <TestAttributesGenerator
          testSteps={testSteps}
          onStepChange={onStepChange}
          onReplaceSteps={onReplaceSteps}
          phase={genPhase}
          progress={genProgress}
          error={genError}
          onGenerate={runGenerate}
        />
      </Section>

      {/* 3. Attribute Testing Table — actions on the header, locked until attributes saved */}
      <Section
        title="Attribute Testing Table"
        open={open.table}
        onToggle={() => toggle('table')}
        locked={!hasAttributes}
        lockMessage={stepsModified ? 'Save test attributes first' : 'Generate test attributes first'}
        icon={iconFor('M3 10h18M3 14h18M10 6h4M10 18h4')}
        actions={
          <>
            {suggestError && (
              <span className="text-xs text-red-200 max-w-xs truncate" title={suggestError}>
                <AlertTriangle className="h-3 w-3 inline mr-1" />{suggestError}
              </span>
            )}
            <button type="button" onClick={suggestFields} disabled={suggesting} className={btnGhost}>
              {suggesting ? <><Loader2 className="h-3 w-3 animate-spin" /> Suggesting…</> : <><Sparkles className="h-3 w-3" /> Suggest Fields</>}
            </button>
            <button type="button" onClick={addField} className={btnGhost}>
              <Plus className="h-3 w-3" /> Add Field
            </button>
            {(fieldsDirty || fieldsSaving) && (
              <button type="button" onClick={saveFields} disabled={fieldsSaving} className={btnWhite}>
                <Save className="h-3 w-3" /> {fieldsSaving ? 'Saving…' : 'Save now'}
              </button>
            )}
          </>
        }
      >
        <AttributeFieldMapping
          fields={fields}
          attributes={(testSteps || []).map((s, i) => ({ id: s.id || String(i), ref: s.ref || String.fromCharCode(65 + i) }))}
          onRename={renameField}
          onDescribe={describeField}
          onDelete={deleteField}
          onScope={scopeField}
          onPassCondition={setPassCondition}
        />
      </Section>

    </div>
  );
}
